import React, { useEffect, useRef } from 'react';
import { useFormikContext } from 'formik';
import { Grid } from '@material-ui/core';
import { lookupsJson, ScheduledFarmItemTypes } from '@ourbranch/lookups';
import { appConfig } from 'brand-config';

import Field from 'core/components/form/form.v2';

/**
 * #34585: the first Coverage F item for an FO-6 scheduled farm property risk, collected on the
 * NEW-OFFER form rather than after the offer exists.
 *
 * The risk has no building and no coverage limit of its own — its scheduled items ARE its entire
 * premium. Clarion Door rates an item-less scheduled risk at $0 and answers `MsgStatus: Success`
 * with a blank error text, which the farm rater turns into BLOCK 5042 "Farm rating error", failing
 * the whole all-or-nothing farm rate on the very first "See price". Collecting one item here is what
 * makes the flow usable at all, exactly as #33164 does for standalone inland marine.
 *
 * Deliberately TRIMMED to the four fields the offer-page add-item form requires for EVERY category.
 * Everything category-specific — livestock type and quantity, machinery make/serial/year, hay stack
 * limit, replacement cost, wind/hail deductible — is nullable on the offer-level schema and is
 * collected on the offer page, where the full item form already lives. A missing wind/hail
 * deductible is safe: the rater derives one via `parseWindHailDeductible`.
 */
const FO6ScheduledRiskDetail = () => {
  const { values, setFieldValue } = useFormikContext();

  const farmLocation = values.farmLocations?.[0];
  const farmRisk = farmLocation?.risks?.[0];
  // The item path is fixed at [0]: the new-offer form collects exactly one item, and further items
  // are added on the offer page.
  const itemPath = 'farmLocations[0].risks[0].coverages.coverageF[0]';

  // Resolve brand config from the LOCATION's state, not the applicant's mailing state — the risk
  // lives at the location, and an out-of-state mailing address otherwise reads the wrong brand
  // config (the #32094 class of bug). Fall back to the mailing state before a location is entered.
  const coverageFState = farmLocation?.address?.state || values.state;
  const coverageFConfig = appConfig.farm?.[coverageFState]?.risks?.FO6Scheduled?.coverageF;

  const item = farmRisk?.coverages?.coverageF?.[0];
  const category = item?.category;

  const categoryOptions = coverageFConfig?.itemTypes || lookupsJson.farm.scheduledFarmPropertyCategories;
  const itemOverrides = coverageFConfig?.itemOverrides?.[category];
  const isLivestock = category === ScheduledFarmItemTypes.LIVESTOCK;
  // Livestock has its own deductible ladder on CFM (#33662). Mirror the offer-page add-item form so
  // the agent cannot pick a deductible here that the offer page then rejects.
  const deductibleOptions =
    (isLivestock && coverageFConfig?.livestockDeductibleOptions) ||
    coverageFConfig?.deductibleOptions ||
    lookupsJson.farm.deductibleAllOther;
  const defaultDeductible =
    (isLivestock && coverageFConfig?.defaultLivestockDeductible) ||
    coverageFConfig?.defaultDeductible ||
    deductibleOptions[0]?.id;

  // `deductible` is non-null on ScheduledFarmPropertyItemDetailsInput, so leaving it unset fails the
  // whole requestQuote mutation. The offer-page add-item form seeds it rather than making the agent
  // pick; do the same here, on two triggers:
  //
  //  - a genuine category CHANGE, because livestock and non-livestock have different deductible
  //    ladders (#33662) — carrying a $5,000 livestock deductible over to Hay would leave a value
  //    that isn't in Hay's own dropdown, and that the offer page would later reject.
  //  - a blank deductible, so a persisted item that never had one still gets filled.
  //
  // A remount is deliberately NOT a trigger, which is why this compares against a ref rather than
  // relying on the dependency array (an effect always fires on mount). `resubmit-offer-modal`
  // reuses InitialRiskDetail on a PERSISTED offer — see the comment at initial-risk-detail.jsx:51 —
  // so this component can mount with a saved, deliberately non-default deductible, and re-seeding
  // there would silently discard it just because the modal was opened. The offer-page dialog can
  // re-seed unconditionally because it owns a fresh Formik that always starts blank and is
  // resetForm()-ed after each submit; this one reads the shared farm-risk form.
  const previousCategory = useRef(category);
  useEffect(() => {
    const categoryChanged = category !== previousCategory.current;
    previousCategory.current = category;
    if (category && (categoryChanged || item?.deductible == null)) {
      setFieldValue(`${itemPath}.deductible`, defaultDeductible);
    }
  }, [category, item?.deductible, defaultDeductible, itemPath, setFieldValue]);

  return (
    <Grid container spacing={2}>
      <Field
        xs={6}
        type="select"
        id={`${itemPath}.category`}
        name={`${itemPath}.category`}
        mode="light"
        label="Type of item*"
        options={categoryOptions}
        fast={false}
      />
      <Field
        xs={6}
        type="string"
        id={`${itemPath}.description`}
        name={`${itemPath}.description`}
        mode="light"
        // Same wording (and the same brand override) as the offer-page item row — the agent fills
        // this field here and then sees it again there, so the two must not disagree.
        label={`${itemOverrides?.descriptionLabel || 'Item name'}*`}
      />
      <Field
        xs={6}
        type="numeric"
        id={`${itemPath}.limit`}
        name={`${itemPath}.limit`}
        mode="light"
        label="Coverage amount*"
        format={{
          allowNegative: false,
          decimalScale: 0,
          thousandSeparator: true,
          prefix: '$'
        }}
      />
      <Field
        xs={6}
        type="select"
        id={`${itemPath}.deductible`}
        name={`${itemPath}.deductible`}
        mode="light"
        label="Deductible*"
        options={deductibleOptions}
        fast={false}
      />
    </Grid>
  );
};

export default FO6ScheduledRiskDetail;
